import {sleep} from '@benchling/js-utils';

import {GenerateDocumentContentMessage} from 'notebook/ai-tools/NotebookAIToolsConstants';
import * as Net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

interface GenerateDocumentContentResponse {
  message: GenerateDocumentContentMessage;
}

interface GenerateDocumentContentResponse {
  message: GenerateDocumentContentMessage;
}

export function generateDocumentContent(
  entryId: string,
  fileS3Locations: Array<string>,
  userPrompt: string,
  notePartIndex: number
): Promise<GenerateDocumentContentResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/notebook/ai-tools:generate-document-content',
    data: {entryId, fileS3Locations, userPrompt, notePartIndex},
  });
}

export interface CheckEntryUsingAIResponse {
  traceId: string;
  checkResults: Array<{text: string}>;
  hadError: boolean;
  errorMessage: string | null;
  checkedAt: string | null;
  checkedVersion: number | null;
}

export interface StructuredTableSubmissionStatus {
  name: string;
  kind: string;
  provenanceUuid: string;
  submitted: boolean;
}

export function checkEntryUsingAI(
  entryId: string,
  structuredTableStatuses?: Array<StructuredTableSubmissionStatus>
): Promise<CheckEntryUsingAIResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/notebook/ai-tools:check-entry',
    data: {entryId, structuredTableStatuses},
  });
}

export interface FillStructuredTableUsingAIRunResultResponse {
  displayName: string;
  modelFamily: string;
  runConfigKey: string;
  newRows: {[tableName: string]: Array<Array<{text: string}>>};
  hadError: boolean;
  durationSeconds: number;
}

export interface FillStructuredTableUsingAIMatchRowResponse {
  row: Array<{text: string}>;
  index: number;
}

export interface FillStructuredTableUsingAIMatchResultResponse {
  row1: FillStructuredTableUsingAIMatchRowResponse | null;
  row2: FillStructuredTableUsingAIMatchRowResponse | null;
  tableName?: string;
}

export interface FillStructuredTableUsingAIPairwiseComparisonResponse {
  runIndex1: number;
  runIndex2: number;
  matchResults: Array<FillStructuredTableUsingAIMatchResultResponse>;
  isExactMatch: boolean;
}

export interface FillStructuredTableUsingAIResponse {
  runResults: Array<FillStructuredTableUsingAIRunResultResponse>;
  pairwiseComparisons: Array<FillStructuredTableUsingAIPairwiseComparisonResponse>;
}

export interface FillStructuredTableUsingAIFile {
  s3Location: string;
  mediaType: string;
}

export enum FillStructuredTableRunStatusEnum {
  QUEUED = 'QUEUED',
  PLANNING = 'PLANNING',
  GENERATING_ROWS = 'GENERATING_ROWS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export interface FillStructuredTableRunStatus {
  displayName: string;
  currentStatus: FillStructuredTableRunStatusEnum;
  results: FillStructuredTableUsingAIRunResultResponse | null;
}

export enum FillStructuredTableStatusEnum {
  QUEUED = 'QUEUED',
  PROCESSING_FILES = 'PROCESSING_FILES',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}
export interface FillStructuredTablePartialResponse {
  status: FillStructuredTableStatusEnum;
  runs: Array<FillStructuredTableRunStatus>;
  pairwiseComparisons: Array<FillStructuredTableUsingAIPairwiseComparisonResponse>;
}

const POLL_TIMEOUT_MS = 1000;

export function fillStructuredTableUsingAIDirect(
  documentId: string,
  tableNames: Array<string>,
  files: Array<FillStructuredTableUsingAIFile>,
  userPrompt: string
): Promise<FillStructuredTableUsingAIResponse> {
  return Net.postJSONPromise({
    url: '/1/api/notebook/ai-tools:fill-structured-table-direct',
    data: {documentId, tableNames, files, userPrompt},
  });
}

export async function fillStructuredTableUsingAI(
  documentId: string,
  tableNames: Array<string>,
  files: Array<FillStructuredTableUsingAIFile>,
  userPrompt: string,
  onStatusReport?: (response: FillStructuredTablePartialResponse) => void,
  useSingleModel?: boolean
): Promise<FillStructuredTableUsingAIResponse> {
  const initialResponse = await Net.postJSONPromise({
    url: '/1/api/notebook/ai-tools:fill-structured-table',
    data: {documentId, tableNames, files, userPrompt, useSingleModel},
  });

  // Use task polling approach for cross-model validation
  const statusURL = initialResponse.status_url;
  let taskStatus = await Net.getJSONPromise(statusURL);

  while (taskStatus.status !== 'SUCCESS' && taskStatus.status !== 'FAILURE') {
    if (taskStatus.partialResult) {
      onStatusReport?.(taskStatus.partialResult);
    }
    await sleep(POLL_TIMEOUT_MS);
    taskStatus = await Net.getJSONPromise(statusURL);
  }

  if (taskStatus.status === 'FAILURE') {
    throw TaskRunner.flagTaskError(TaskRunner.getFailedTaskErrorResponse(taskStatus));
  }
  return taskStatus.result;
}

export interface SuggestDataEntryClarificationsResponse {
  suggestedFirstRows: Array<Array<{text: string}>> | null;
  tableOrder?: Array<string>;
  suggestedPrompt?: string;
  errorMessage: string | null;
  hadError: boolean;
}

export function suggestDataEntryClarifications(
  documentId: string,
  tableNames: Array<string>,
  files: Array<FillStructuredTableUsingAIFile>,
  userPrompt: string,
  includeSuggestedPrompt: boolean
): Promise<SuggestDataEntryClarificationsResponse> {
  return Net.postJSONPromise({
    url: '/1/api/notebook/ai-tools:suggest-data-entry-clarifications',
    data: {documentId, tableNames, files, userPrompt, includeSuggestedPrompt},
  });
}
